import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { autoUploadPromo } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { skippedPayment } from 'reactApp/appHelpers/featuresHelpers/features/skippedPayments';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { createFamilyAfterPaid } from 'reactApp/modules/family/sagas/createFamily.saga';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { AUTOUPLOAD_10TB_REGEX, SPACE_SIZES } from 'reactApp/modules/products/products.helpers';
import { getMultiTariffMobileActiveTab, QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isEmailAbsent } from 'reactApp/modules/socialUser/socialUser.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendWebviewAnalytics } from 'reactApp/sections/MobileQuotaTariffsLanding/MobileQuotaTariffsLanding.helpers';
import { EPostMessageBuy } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.types';
import { openAutoUploadBuyDialog } from 'reactApp/sections/QuotaLanding/AutoUploadBuyDialog/AutoUploadBuyDialog.helpers';
import { getQuotaBillingSource, sendQuotaGa, sendWVRadar, sendXrayWithPlatform } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { openSkippedPaymentModal } from 'reactApp/sections/QuotaLanding/SkippedPaymentDialog/SkippedPaymentDialog.helpers';
import { getPaymentData, revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import type { Product } from 'reactApp/types/Billing';
import { renderConfirmEmailMobile } from 'reactApp/ui/ConfirmEmail/ConfirmEmailMobile/ConfirmEmailMobile.helpers';
import { sendAdmitadProduct } from 'reactApp/utils/admitadHelper';
import { sendXray } from 'reactApp/utils/ga';
import { scrollToTop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';
import { sendPostMessage } from 'reactApp/utils/windowHelper';

import type { IMobilePayProps, TBuyClick } from './TariffPlansCard.types';

export const sendClickAnalytics = ({ product, paySource, isAlmostFullFreeUser = false, isReferral = false, tariffTab = '' }) => {
    emitAnalyticEvent(AnalyticEventNames.TARIFF_CLICK, { paySource, product, isAlmostFullFreeUser, isReferral });
    sendQuotaGa({
        action: 'tariff',
        label: 'click',
        tariff: product.id,
        amount: product.price,
        name_button_from: tariffTab,
        paySource,
    });
    sendTmrGoal({ id: TMR3473039, goal: 'tariff-click' });
};

export const useBuyClick = ({
    isMobilePayAvailable,
    isMobilePayFromGetParam,
    isIOSPromo = false,
    isAndroidPromo = false,
    enabledMidasPayment = false,
}: IMobilePayProps): TBuyClick => {
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const isSocial = useSelector(UserSelectors.isSocialUser);
    const isSocialUserWithoutEmail = useSelector(isEmailAbsent);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isAlmostFullFreeUser = useSelector(UserSelectors.isAlmostFullFreeUser);
    // tempexp_18086-next-line
    const hasRefId = useSelector(ReferralProgramSelectors.getRefId);

    // tempexp_19341-next-line
    const activeMultiTariffTab = useSelector(getMultiTariffMobileActiveTab);

    const { isQuotaTariffs, isFamilyLanding } = getStorage(storage);

    // tempexp_18727-start
    const isUserWithoutPayment = useSelector(BillingDataSelectors.isUserWithoutPayment);
    const handleSkippedPayment = skippedPayment && isUserWithoutPayment;
    // tempexp_18727-end

    const openBuy = useCallback(
        // tempexp_16481-next-line
        ({ product, paySource, onClick, space, enabledTrialMidasPayment }) => {
            const onSuccess = () => {
                sendAdmitadProduct(product);
                sendWebviewAnalytics('payok', isMobilePayAvailable);
                sendWVRadar('quota_land_wv_payok', { isIOSPromo, isAndroidPromo, isMobilePayAvailable, isMobilePayFromGetParam });
                emitAnalyticEvent(AnalyticEventNames.TARIFF_BUY, { paySource, product, enabledMidasPayment, isAlmostFullFreeUser });
                sendQuotaGa({
                    action: 'tariff',
                    label: 'buy',
                    tariff: product.id,
                    amount: product.price,
                    paySource,
                });
                sendTmrGoal({ id: TMR3473039, goal: 'tariff-buy' });

                // tempexp_16481-start
                if (AUTOUPLOAD_10TB_REGEX.test(product.id)) {
                    sendXrayWithPlatform(['autoupload-10tb', `tariff-buy-${space}`]);
                }
                // tempexp_16481-end

                if (isMobile) {
                    sendPostMessage({
                        paymentResult: EPostMessageBuy.SUCCESS,
                        productId: product.id,
                        price: product.price,
                        family: product.isFamily,
                    });
                }

                if (autoUploadPromo && !isFamilyLanding && !product.isFamily && product?.space?.original >= SPACE_SIZES._256) {
                    openAutoUploadBuyDialog();
                }

                /**
                 * CLOUD-4653: Для семейной подписки делаем редирект на Хомяк с параметром для открытия модалки
                 */
                if (product.isFamily || isFamilyLanding) {
                    if (isFamilyLanding) {
                        sendXray('lnd-fam-pmnt', 'success');
                    } else {
                        sendXray('lnd-quota', 'family-tariff-cr-fam-entr');
                    }

                    /**
                     * CLOUD-4641: Дергаем API для создания семьи, если тариф из списка, обход пока решение на беке не сделают
                     */
                    if (IS_WEBVIEW) {
                        setTimeout(() => {
                            sendXray('lnd-quota', 'family-tariff-cr-fam-time');
                            dispatch(createFamilyAfterPaid);
                        }, 10000);
                        scrollToTop();
                    } else {
                        opener('/home/?action=new-family-tariff-success', true);
                    }
                } else {
                    scrollToTop();
                }
            };

            const onError = () => {
                sendWebviewAnalytics('payerror', isMobilePayAvailable);
                sendWVRadar('quota_land_wv_payerror', { isIOSPromo, isAndroidPromo, isMobilePayAvailable, isMobilePayFromGetParam });
                sendQuotaGa({ action: 'tariff', label: 'buy-error', tariff: product.id, amount: product.price });

                if (isMobile) {
                    sendPostMessage({
                        paymentResult: EPostMessageBuy.FAILED,
                    });
                }

                if (isFamilyLanding) {
                    sendXray('lnd-fam-pmnt', 'error');
                }
            };

            // tempexp_18727-start
            const onSkipPayment = () => {
                openSkippedPaymentModal({ product: product as Product });
            };
            // tempexp_18727-end

            const { title, paymentDescription, subTitle } = getPaymentData(product, enabledMidasPayment);
            dispatch(
                openTariffBuy({
                    productId: product.id,
                    source: getQuotaBillingSource(),
                    checkAnonymous: true,
                    onSuccess,
                    isMobile,
                    onError,
                    paySource,
                    onClick,
                    subTitle,
                    enabledMidasPayment,
                    title,
                    paymentDescription,
                    enabledTrialMidasPayment,
                    ...(handleSkippedPayment && !product?.hasTrial && { onSkipPayment }),
                })
            );
        },
        [
            dispatch,
            enabledMidasPayment,
            isAndroidPromo,
            isIOSPromo,
            isMobile,
            isMobilePayAvailable,
            isMobilePayFromGetParam,
            isAlmostFullFreeUser,
            handleSkippedPayment,
        ]
    );

    return useCallback(
        // tempexp_16481-next-line
        ({ product, paySource, onClick, space, skipClickAnalytics = false, enabledTrialMidasPayment = false }) => {
            sendWebviewAnalytics('tclick', isMobilePayAvailable);
            sendWVRadar('quota_land_wv_tclick', { isIOSPromo, isAndroidPromo, isMobilePayAvailable, isMobilePayFromGetParam });

            if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
                revealPlatformPopup(product.id, isIOSPromo, isAndroidPromo);
                return;
            }

            // Социальным пользователям показываем окно привязки почты
            if (isQuotaTariffs && isMobile && isSocial && isSocialUserWithoutEmail) {
                // tempexp_16481-next-line
                renderConfirmEmailMobile({ onSuccess: () => openBuy({ product, paySource, space }) });
                return;
            }

            if (!skipClickAnalytics) {
                sendClickAnalytics({ product, paySource, isAlmostFullFreeUser, isReferral: !!hasRefId, tariffTab: activeMultiTariffTab });
            }

            if (isAnonymous) {
                sendTmrGoal({ id: TMR3473039, goal: 'login-view' });
            }

            // tempexp_16481-next-line
            openBuy({ product, paySource, onClick, space, enabledTrialMidasPayment });

            // tempexp_16481-start
            if (AUTOUPLOAD_10TB_REGEX.test(product.id)) {
                sendXrayWithPlatform(['autoupload-10tb', `tariff-click-${space}`]);
            }
            // tempexp_16481-end
        },
        [
            isIOSPromo,
            isAndroidPromo,
            isMobilePayAvailable,
            isMobilePayFromGetParam,
            isQuotaTariffs,
            isMobile,
            isSocialUserWithoutEmail,
            openBuy,
            hasRefId,
            activeMultiTariffTab,
        ]
    );
};
